import { useCallback, useMemo, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { useRealtimeChannel } from '@/hooks/use-realtime-channel';
import { useToast } from '@/hooks/use-toast';

import {
  invalidateRevocationListCaches,
  isCorrespondenceActiveAccessPayload,
  isCorrespondenceRevocationPayload,
  isSharedAccountSharePayload,
  readShareRevocationEvent,
  removeCorrespondenceAccessCaches,
} from './correspondence-share-access';
import {
  catchUpTaskShareAccess,
  evictRevokedTaskShare,
  isTaskActiveAccessPayload,
  isTaskRevocationPayload,
  readTaskShareEvent,
  refreshTaskShareAccess,
} from './task-share-access';
import { readShareString } from './share-payload-utils';
import {
  ShareAccessRealtimeContext,
  type CorrespondenceShareListener,
  type ShareAccessRealtimeContextValue,
} from './share-access-events';
import { shareSnapshotKey } from './share-access-reconciliation';
import { useShareAccessReconciliation } from './use-share-access-reconciliation';

import type { ReactElement, ReactNode } from 'react';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

/**
 * App-root realtime provider for recipient-side share-access changes.
 *
 * Owns ONE per-user postgres_changes channel multiplexing correspondence_shares
 * AND task_shares (both filtered `shared_with=eq.<uid>`), so a grant / revoke
 * evicts the recipient's cached constituent content NO MATTER which page is
 * open — fixing the prior panel/page-scoped subscriptions that dropped eviction
 * whenever the recipient was elsewhere in the app (RC-5). The recipient RLS
 * delegate policies deliberately expose revoked rows so the soft-revoke UPDATE
 * is delivered. See docs/plans/2026-07-04-sharing-realtime-enterprise-hardening-plan.md.
 *
 * The correspondence detail panel's own "blank the open thread the instant its
 * share is revoked" UX is preserved via `useCorrespondenceShareEvents` (share-access-events.ts): the
 * panel registers a listener rather than opening a second subscription.
 */

type ToastFn = ReturnType<typeof useToast>['toast'];
type TranslateFn = ReturnType<typeof useTranslation>['t'];

function handleCorrespondencePayload(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
  queryClient: QueryClient,
  toast: ToastFn,
  notify: CorrespondenceShareListener,
  t: TranslateFn,
): void {
  // A share on a shared mailbox never changes access, so neither its grant
  // nor its revocation may evict caches or announce "Access removed".
  if (isSharedAccountSharePayload(payload, queryClient)) {
    return;
  }

  // Fire only on a real null-to-timestamp transition. `undefined` (REPLICA
  // IDENTITY skipped the column) is "unknown" and short-circuits; the migration
  // sets REPLICA IDENTITY FULL on correspondence_shares so null is the pre-revoke
  // state, not an absent column.
  if (isCorrespondenceActiveAccessPayload(payload)) {
    const event = readShareRevocationEvent(payload);
    removeCorrespondenceAccessCaches(queryClient, event);
    notify(event, 'granted');
    invalidateRevocationListCaches(queryClient, event);
    return;
  }

  if (!isCorrespondenceRevocationPayload(payload)) {
    return;
  }

  const event = readShareRevocationEvent(payload);
  removeCorrespondenceAccessCaches(queryClient, event);
  notify(event, 'revoked');
  invalidateRevocationListCaches(queryClient, event);
  toast({
    title: t('shareAccessRealtime.accessRemovedTitle'),
    description: t('shareAccessRealtime.correspondenceRevokedDescription'),
  });
}

function handleTaskPayload(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
  queryClient: QueryClient,
  toast: ToastFn,
  t: TranslateFn,
): void {
  if (isTaskActiveAccessPayload(payload)) {
    refreshTaskShareAccess(queryClient, readTaskShareEvent(payload));
    return;
  }

  if (!isTaskRevocationPayload(payload)) {
    return;
  }

  evictRevokedTaskShare(queryClient, readTaskShareEvent(payload));
  toast({
    title: t('shareAccessRealtime.accessRemovedTitle'),
    description: t('shareAccessRealtime.taskRevokedDescription'),
  });
}

export function ShareAccessRealtimeProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { toast } = useToast();
  const { t } = useTranslation('common');
  const userId = user?.id;
  const organizationId = currentOrganization?.id;

  // Listeners for the correspondence detail panel's local revoke/grant UX. A
  // Set (not the callback in a dep array) so registering/unregistering never
  // churns the realtime channel.
  const listenersRef = useRef<Set<CorrespondenceShareListener>>(new Set());
  const subscribeToCorrespondenceEvents = useCallback((listener: CorrespondenceShareListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);
  const notifyCorrespondence = useCallback<CorrespondenceShareListener>((event, kind) => {
    for (const listener of listenersRef.current) {
      listener(event, kind);
    }
  }, []);

  // A missed revoke never toasts here: the realtime path may already have.
  const notifyMissedRevoke = useCallback(
    (correspondenceId: string, orgId: string) => {
      notifyCorrespondence({ correspondenceId, organizationId: orgId }, 'revoked');
    },
    [notifyCorrespondence],
  );
  useShareAccessReconciliation({ organizationId, userId, onRevoked: notifyMissedRevoke });

  useRealtimeChannel({
    channelName: userId ? `share-access-${userId}` : 'share-access',
    subscriptions: userId
      ? [
          { event: 'INSERT', table: 'correspondence_shares', filter: `shared_with=eq.${userId}` },
          { event: 'UPDATE', table: 'correspondence_shares', filter: `shared_with=eq.${userId}` },
          { event: 'DELETE', table: 'correspondence_shares', filter: `shared_with=eq.${userId}` },
          { event: 'INSERT', table: 'task_shares', filter: `shared_with=eq.${userId}` },
          { event: 'UPDATE', table: 'task_shares', filter: `shared_with=eq.${userId}` },
          { event: 'DELETE', table: 'task_shares', filter: `shared_with=eq.${userId}` },
        ]
      : [],
    onPayload: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
      // Defense-in-depth cross-user guard: on a user switch (a shared government
      // workstation — A signs out, B signs in) the old channel tears down async,
      // and a payload already in flight can be delivered to B's provider instance
      // because useRealtimeChannel does NOT generation-guard postgres_changes
      // callbacks (only the status callback). Both share tables carry
      // REPLICA IDENTITY FULL, so shared_with is always present; drop any payload
      // not addressed to the current user before touching a single cache.
      const sharedWith =
        readShareString(payload.new, 'shared_with') ?? readShareString(payload.old, 'shared_with');
      if (sharedWith !== userId) {
        return;
      }

      if (payload.table === 'correspondence_shares') {
        handleCorrespondencePayload(payload, queryClient, toast, notifyCorrespondence, t);
        // Re-read the snapshot so a share granted between two reads is in the
        // baseline when its revoke is the event that goes missing.
        if (organizationId && userId && !isSharedAccountSharePayload(payload, queryClient)) {
          queryClient
            .invalidateQueries({ queryKey: shareSnapshotKey(organizationId, userId) })
            .catch(() => false);
        }
      } else if (payload.table === 'task_shares') {
        handleTaskPayload(payload, queryClient, toast, t);
      }
    },
    // Reconnect catch-up: a grant / revoke that fired while the socket was down
    // is lost (postgres_changes has no replay; the global query client does not
    // refetch-on-reconnect). Refetch both domains' LIST surfaces under current
    // RLS. The correspondence half is gated on organizationId (all-or-nothing —
    // invalidateRevocationListCaches would otherwise silently skip the org-scoped
    // surfaces); the task half needs no org. Reconciling revocations missed while
    // the app was fully CLOSED (persisted correspondence PII on reload) is WP-3,
    // which removes correspondence caches from localStorage at the root. The
    // snapshot re-read then evicts each thread whose share ended meanwhile.
    onResubscribed: userId
      ? () => {
          if (organizationId) {
            invalidateRevocationListCaches(queryClient, {
              correspondenceId: undefined,
              organizationId,
            });
            queryClient
              .invalidateQueries({ queryKey: shareSnapshotKey(organizationId, userId) })
              .catch(() => false);
          }
          catchUpTaskShareAccess(queryClient);
        }
      : undefined,
    enabled: !!userId,
  });

  const value = useMemo<ShareAccessRealtimeContextValue>(
    () => ({ subscribeToCorrespondenceEvents }),
    [subscribeToCorrespondenceEvents],
  );

  return (
    <ShareAccessRealtimeContext.Provider value={value}>
      {children}
    </ShareAccessRealtimeContext.Provider>
  );
}
